/**
 * LeaderService
 * Service for managing care leaders (keyed by userId) including coverage
 * (zones/needs/locations), availability and documents
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for care leader operations (enriched views, coverage replacement, documents)
 */
export class LeaderService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? LeaderService.DEFAULT_SERVICE_PATH,
            basePath: LeaderService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * List leaders, optionally filtered
     * @param typeId - Optional LeaderType ID filter
     * @param zoneId - Optional CareZone ID filter
     * @param needId - Optional CareNeed ID filter
     * @param search - Optional free-text search
     * @returns Array of leader model views
     */
    async list(typeId, zoneId, needId, search) {
        const response = await this.listRaw(typeId, zoneId, needId, search);
        return response.data ?? [];
    }
    /**
     * List leaders with full response
     * @param typeId - Optional LeaderType ID filter
     * @param zoneId - Optional CareZone ID filter
     * @param needId - Optional CareNeed ID filter
     * @param search - Optional free-text search
     * @returns ResponseData with array of leader model views
     */
    listRaw(typeId, zoneId, needId, search, skip, take) {
        const url = this.buildUrlWithParams(undefined, {
            typeId,
            zoneId,
            needId,
            search,
            skip,
            take,
        });
        return this.http.get(url);
    }
    /**
     * Get the current authenticated user's leader profile
     * @returns Leader model view for the current user
     */
    async me() {
        const response = await this.meRaw();
        if (!response.data) {
            throw new Error('Leader profile for the current user not found');
        }
        return response.data;
    }
    /**
     * Get the current authenticated user's leader profile with full response
     * @returns ResponseData with leader model view
     */
    meRaw() {
        const url = this.buildUrl('me');
        return this.http.get(url);
    }
    /**
     * Get a leader by user ID
     * @param userId - The leader user ID
     * @returns Leader model view
     */
    async getByUserId(userId) {
        const response = await this.getByUserIdRaw(userId);
        if (!response.data) {
            throw new Error(`Leader with user ID ${userId} not found`);
        }
        return response.data;
    }
    /**
     * Get a leader by user ID with full response
     * @param userId - The leader user ID
     * @returns ResponseData with leader model view
     */
    getByUserIdRaw(userId) {
        const url = this.buildUrl(userId);
        return this.http.get(url);
    }
    /**
     * Create a leader
     * @param data - Create payload (coverage lists optional)
     * @returns The created leader
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create leader');
        }
        return response.data;
    }
    /**
     * Create a leader with full response
     * @param data - Create payload
     * @returns ResponseData with the created leader
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    /**
     * Update a leader
     * @param userId - The leader user ID
     * @param data - Update payload
     * @returns The updated leader
     */
    async update(userId, data) {
        const response = await this.updateRaw(userId, data);
        if (!response.data) {
            throw new Error(`Failed to update leader with user ID ${userId}`);
        }
        return response.data;
    }
    /**
     * Update a leader with full response
     * @param userId - The leader user ID
     * @param data - Update payload
     * @returns ResponseData with the updated leader
     */
    updateRaw(userId, data) {
        const url = this.buildUrl(userId);
        return this.http.put(url, data);
    }
    /**
     * Delete a leader
     * @param userId - The leader user ID
     * @returns True if deleted successfully
     */
    async delete(userId) {
        const response = await this.deleteRaw(userId);
        return response.success;
    }
    /**
     * Delete a leader with full response
     * @param userId - The leader user ID
     * @returns ResponseData (success/fail)
     */
    deleteRaw(userId) {
        const url = this.buildUrl(userId);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Zones
    // ---------------------------------------------------------------------------
    /**
     * List the zones a leader covers
     * @param userId - The leader user ID
     * @returns Array of leader zone views
     */
    async getZones(userId) {
        const response = await this.getZonesRaw(userId);
        return response.data ?? [];
    }
    /**
     * List the zones a leader covers with full response
     * @param userId - The leader user ID
     * @returns ResponseData with array of leader zone views
     */
    getZonesRaw(userId) {
        const url = this.buildUrl(`${userId}/zones`);
        return this.http.get(url);
    }
    /**
     * Add a zone coverage row to a leader
     * @param userId - The leader user ID
     * @param body - Zone request payload
     * @returns The created leader zone
     */
    async addZone(userId, body) {
        const response = await this.addZoneRaw(userId, body);
        if (!response.data) {
            throw new Error(`Failed to add zone to leader ${userId}`);
        }
        return response.data;
    }
    /**
     * Add a zone coverage row to a leader with full response
     * @param userId - The leader user ID
     * @param body - Zone request payload
     * @returns ResponseData with the created leader zone
     */
    addZoneRaw(userId, body) {
        const url = this.buildUrl(`${userId}/zones`);
        return this.http.post(url, body);
    }
    /**
     * Update a leader's zone coverage row
     * @param userId - The leader user ID
     * @param leaderZoneId - The leader zone ID
     * @param body - Zone request payload
     * @returns The updated leader zone
     */
    async updateZone(userId, leaderZoneId, body) {
        const response = await this.updateZoneRaw(userId, leaderZoneId, body);
        if (!response.data) {
            throw new Error(`Failed to update zone ${leaderZoneId} for leader ${userId}`);
        }
        return response.data;
    }
    /**
     * Update a leader's zone coverage row with full response
     * @param userId - The leader user ID
     * @param leaderZoneId - The leader zone ID
     * @param body - Zone request payload
     * @returns ResponseData with the updated leader zone
     */
    updateZoneRaw(userId, leaderZoneId, body) {
        const url = this.buildUrl(`${userId}/zones/${leaderZoneId}`);
        return this.http.put(url, body);
    }
    /**
     * Remove a leader's zone coverage row
     * @param userId - The leader user ID
     * @param leaderZoneId - The leader zone ID
     * @returns True if removed successfully
     */
    async removeZone(userId, leaderZoneId) {
        const response = await this.removeZoneRaw(userId, leaderZoneId);
        return response.success;
    }
    /**
     * Remove a leader's zone coverage row with full response
     * @param userId - The leader user ID
     * @param leaderZoneId - The leader zone ID
     * @returns ResponseData (success/fail)
     */
    removeZoneRaw(userId, leaderZoneId) {
        const url = this.buildUrl(`${userId}/zones/${leaderZoneId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Needs
    // ---------------------------------------------------------------------------
    /**
     * List the needs a leader addresses
     * @param userId - The leader user ID
     * @returns Array of leader need views
     */
    async getNeeds(userId) {
        const response = await this.getNeedsRaw(userId);
        return response.data ?? [];
    }
    /**
     * List the needs a leader addresses with full response
     * @param userId - The leader user ID
     * @returns ResponseData with array of leader need views
     */
    getNeedsRaw(userId) {
        const url = this.buildUrl(`${userId}/needs`);
        return this.http.get(url);
    }
    /**
     * Add a need capability row to a leader
     * @param userId - The leader user ID
     * @param body - Need request payload
     * @returns The created leader need
     */
    async addNeed(userId, body) {
        const response = await this.addNeedRaw(userId, body);
        if (!response.data) {
            throw new Error(`Failed to add need to leader ${userId}`);
        }
        return response.data;
    }
    /**
     * Add a need capability row to a leader with full response
     * @param userId - The leader user ID
     * @param body - Need request payload
     * @returns ResponseData with the created leader need
     */
    addNeedRaw(userId, body) {
        const url = this.buildUrl(`${userId}/needs`);
        return this.http.post(url, body);
    }
    /**
     * Update a leader's need capability row
     * @param userId - The leader user ID
     * @param leaderNeedId - The leader need ID
     * @param body - Need request payload
     * @returns The updated leader need
     */
    async updateNeed(userId, leaderNeedId, body) {
        const response = await this.updateNeedRaw(userId, leaderNeedId, body);
        if (!response.data) {
            throw new Error(`Failed to update need ${leaderNeedId} for leader ${userId}`);
        }
        return response.data;
    }
    /**
     * Update a leader's need capability row with full response
     * @param userId - The leader user ID
     * @param leaderNeedId - The leader need ID
     * @param body - Need request payload
     * @returns ResponseData with the updated leader need
     */
    updateNeedRaw(userId, leaderNeedId, body) {
        const url = this.buildUrl(`${userId}/needs/${leaderNeedId}`);
        return this.http.put(url, body);
    }
    /**
     * Remove a leader's need capability row
     * @param userId - The leader user ID
     * @param leaderNeedId - The leader need ID
     * @returns True if removed successfully
     */
    async removeNeed(userId, leaderNeedId) {
        const response = await this.removeNeedRaw(userId, leaderNeedId);
        return response.success;
    }
    /**
     * Remove a leader's need capability row with full response
     * @param userId - The leader user ID
     * @param leaderNeedId - The leader need ID
     * @returns ResponseData (success/fail)
     */
    removeNeedRaw(userId, leaderNeedId) {
        const url = this.buildUrl(`${userId}/needs/${leaderNeedId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Locations
    // ---------------------------------------------------------------------------
    /**
     * List the locations a leader serves
     * @param userId - The leader user ID
     * @returns Array of leader location views
     */
    async getLocations(userId) {
        const response = await this.getLocationsRaw(userId);
        return response.data ?? [];
    }
    /**
     * List the locations a leader serves with full response
     * @param userId - The leader user ID
     * @returns ResponseData with array of leader location views
     */
    getLocationsRaw(userId) {
        const url = this.buildUrl(`${userId}/locations`);
        return this.http.get(url);
    }
    /**
     * Add a location service row to a leader
     * @param userId - The leader user ID
     * @param body - Location request payload
     * @returns The created leader location
     */
    async addLocation(userId, body) {
        const response = await this.addLocationRaw(userId, body);
        if (!response.data) {
            throw new Error(`Failed to add location to leader ${userId}`);
        }
        return response.data;
    }
    /**
     * Add a location service row to a leader with full response
     * @param userId - The leader user ID
     * @param body - Location request payload
     * @returns ResponseData with the created leader location
     */
    addLocationRaw(userId, body) {
        const url = this.buildUrl(`${userId}/locations`);
        return this.http.post(url, body);
    }
    /**
     * Update a leader's location service row
     * @param userId - The leader user ID
     * @param leaderLocationId - The leader location ID
     * @param body - Location request payload
     * @returns The updated leader location
     */
    async updateLocation(userId, leaderLocationId, body) {
        const response = await this.updateLocationRaw(userId, leaderLocationId, body);
        if (!response.data) {
            throw new Error(`Failed to update location ${leaderLocationId} for leader ${userId}`);
        }
        return response.data;
    }
    /**
     * Update a leader's location service row with full response
     * @param userId - The leader user ID
     * @param leaderLocationId - The leader location ID
     * @param body - Location request payload
     * @returns ResponseData with the updated leader location
     */
    updateLocationRaw(userId, leaderLocationId, body) {
        const url = this.buildUrl(`${userId}/locations/${leaderLocationId}`);
        return this.http.put(url, body);
    }
    /**
     * Remove a leader's location service row
     * @param userId - The leader user ID
     * @param leaderLocationId - The leader location ID
     * @returns True if removed successfully
     */
    async removeLocation(userId, leaderLocationId) {
        const response = await this.removeLocationRaw(userId, leaderLocationId);
        return response.success;
    }
    /**
     * Remove a leader's location service row with full response
     * @param userId - The leader user ID
     * @param leaderLocationId - The leader location ID
     * @returns ResponseData (success/fail)
     */
    removeLocationRaw(userId, leaderLocationId) {
        const url = this.buildUrl(`${userId}/locations/${leaderLocationId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Availability
    // ---------------------------------------------------------------------------
    /**
     * List a leader's weekly availability slots
     * @param userId - The leader user ID
     * @returns Array of care availability views
     */
    async getAvailability(userId) {
        const response = await this.getAvailabilityRaw(userId);
        return response.data ?? [];
    }
    /**
     * List a leader's weekly availability slots with full response
     * @param userId - The leader user ID
     * @returns ResponseData with array of care availability views
     */
    getAvailabilityRaw(userId) {
        const url = this.buildUrl(`${userId}/availability`);
        return this.http.get(url);
    }
    /**
     * Add an availability slot to a leader
     * @param userId - The leader user ID
     * @param body - Availability slot payload
     * @returns The created availability slot
     */
    async addAvailabilitySlot(userId, body) {
        const response = await this.addAvailabilitySlotRaw(userId, body);
        if (!response.data) {
            throw new Error(`Failed to add availability slot to leader ${userId}`);
        }
        return response.data;
    }
    /**
     * Add an availability slot to a leader with full response
     * @param userId - The leader user ID
     * @param body - Availability slot payload
     * @returns ResponseData with the created availability slot
     */
    addAvailabilitySlotRaw(userId, body) {
        const url = this.buildUrl(`${userId}/availability`);
        return this.http.post(url, body);
    }
    // ----------------------------------------------------------------
    // Bulk replace (whole-set) — kept alongside the individual methods
    // for callers that prefer to set a leader's full coverage in one call.
    // ----------------------------------------------------------------
    /** Replace the leader's full set of zones with the given zone IDs. */
    async replaceZones(userId, zoneIds) {
        await this.replaceZonesRaw(userId, zoneIds);
    }
    replaceZonesRaw(userId, zoneIds) {
        const url = this.buildUrl(`${userId}/zones`);
        return this.http.put(url, zoneIds);
    }
    /** Replace the leader's full set of needs with the given need IDs. */
    async replaceNeeds(userId, needIds) {
        await this.replaceNeedsRaw(userId, needIds);
    }
    replaceNeedsRaw(userId, needIds) {
        const url = this.buildUrl(`${userId}/needs`);
        return this.http.put(url, needIds);
    }
    /** Replace the leader's full set of service locations with the given location IDs. */
    async replaceLocations(userId, locationIds) {
        await this.replaceLocationsRaw(userId, locationIds);
    }
    replaceLocationsRaw(userId, locationIds) {
        const url = this.buildUrl(`${userId}/locations`);
        return this.http.put(url, locationIds);
    }
    /** Replace the leader's full weekly availability with the given slots. */
    async replaceAvailability(userId, body) {
        const response = await this.replaceAvailabilityRaw(userId, body);
        return response.data ?? [];
    }
    replaceAvailabilityRaw(userId, body) {
        const url = this.buildUrl(`${userId}/availability`);
        return this.http.put(url, body);
    }
    /**
     * Update a leader's availability slot
     * @param userId - The leader user ID
     * @param availabilityId - The availability ID
     * @param body - Availability slot payload
     * @returns The updated availability slot
     */
    async updateAvailabilitySlot(userId, availabilityId, body) {
        const response = await this.updateAvailabilitySlotRaw(userId, availabilityId, body);
        if (!response.data) {
            throw new Error(`Failed to update availability slot ${availabilityId} for leader ${userId}`);
        }
        return response.data;
    }
    /**
     * Update a leader's availability slot with full response
     * @param userId - The leader user ID
     * @param availabilityId - The availability ID
     * @param body - Availability slot payload
     * @returns ResponseData with the updated availability slot
     */
    updateAvailabilitySlotRaw(userId, availabilityId, body) {
        const url = this.buildUrl(`${userId}/availability/${availabilityId}`);
        return this.http.put(url, body);
    }
    /**
     * Remove a leader's availability slot
     * @param userId - The leader user ID
     * @param availabilityId - The availability ID
     * @returns True if removed successfully
     */
    async removeAvailabilitySlot(userId, availabilityId) {
        const response = await this.removeAvailabilitySlotRaw(userId, availabilityId);
        return response.success;
    }
    /**
     * Remove a leader's availability slot with full response
     * @param userId - The leader user ID
     * @param availabilityId - The availability ID
     * @returns ResponseData (success/fail)
     */
    removeAvailabilitySlotRaw(userId, availabilityId) {
        const url = this.buildUrl(`${userId}/availability/${availabilityId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Documents
    // ---------------------------------------------------------------------------
    /**
     * List a leader's documents
     * @param userId - The leader user ID
     * @returns Array of leader document views
     */
    async getDocuments(userId) {
        const response = await this.getDocumentsRaw(userId);
        return response.data ?? [];
    }
    /**
     * List a leader's documents with full response
     * @param userId - The leader user ID
     * @returns ResponseData with array of leader document views
     */
    getDocumentsRaw(userId) {
        const url = this.buildUrl(`${userId}/documents`);
        return this.http.get(url);
    }
    /**
     * Add a document to a leader
     * @param userId - The leader user ID
     * @param body - Document request payload
     * @returns The created leader document
     */
    async addDocument(userId, body) {
        const response = await this.addDocumentRaw(userId, body);
        if (!response.data) {
            throw new Error(`Failed to attach document to leader ${userId}`);
        }
        return response.data;
    }
    /**
     * Add a document to a leader with full response
     * @param userId - The leader user ID
     * @param body - Document request payload
     * @returns ResponseData with the created leader document
     */
    addDocumentRaw(userId, body) {
        const url = this.buildUrl(`${userId}/documents`);
        return this.http.post(url, body);
    }
    /**
     * Update a leader's document
     * @param userId - The leader user ID
     * @param documentId - The document ID
     * @param body - Document request payload
     * @returns The updated leader document
     */
    async updateDocument(userId, documentId, body) {
        const response = await this.updateDocumentRaw(userId, documentId, body);
        if (!response.data) {
            throw new Error(`Failed to update document ${documentId} for leader ${userId}`);
        }
        return response.data;
    }
    /**
     * Update a leader's document with full response
     * @param userId - The leader user ID
     * @param documentId - The document ID
     * @param body - Document request payload
     * @returns ResponseData with the updated leader document
     */
    updateDocumentRaw(userId, documentId, body) {
        const url = this.buildUrl(`${userId}/documents/${documentId}`);
        return this.http.put(url, body);
    }
    /**
     * Remove a leader's document
     * @param userId - The leader user ID
     * @param documentId - The document ID
     * @returns True if removed successfully
     */
    async removeDocument(userId, documentId) {
        const response = await this.removeDocumentRaw(userId, documentId);
        return response.success;
    }
    /**
     * Remove a leader's document with full response
     * @param userId - The leader user ID
     * @param documentId - The document ID
     * @returns ResponseData (success/fail)
     */
    removeDocumentRaw(userId, documentId) {
        const url = this.buildUrl(`${userId}/documents/${documentId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Cases
    // ---------------------------------------------------------------------------
    /**
     * List the cases a leader is assigned to
     * @param userId - The leader user ID
     * @returns Array of leader case views
     */
    async getCases(userId) {
        const response = await this.getCasesRaw(userId);
        return response.data ?? [];
    }
    /**
     * List the cases a leader is assigned to with full response
     * @param userId - The leader user ID
     * @returns ResponseData with array of leader case views
     */
    getCasesRaw(userId) {
        const url = this.buildUrl(`${userId}/cases`);
        return this.http.get(url);
    }
    /**
     * Get per-leader caseload statistics for the admin overview
     * @returns Array of leader caseload rows, descending by activeCount
     */
    async caseloadStats() {
        const response = await this.caseloadStatsRaw();
        return response.data ?? [];
    }
    /**
     * Get per-leader caseload statistics with full response
     * @returns ResponseData with array of leader caseload rows
     */
    caseloadStatsRaw() {
        const url = this.buildUrl('stats/caseload');
        return this.http.get(url);
    }
}
LeaderService.DEFAULT_SERVICE_PATH = 'ac-counselling';
LeaderService.BASE_PATH = 'api/leaders';
//# sourceMappingURL=LeaderService.js.map